<script lang="ts">
  import { onMount } from 'svelte';
  import Icon from './Icon.svelte';
  import {
    fetchSnapshots,
    createSnapshot,
    restoreSnapshot,
    deleteSnapshot,
    type DataStructureSnapshot,
  } from '../lib/data-tables-api';

  let { slug, onChanged }: { slug: string; onChanged?: () => void } = $props();

  let items = $state<DataStructureSnapshot[]>([]);
  let loading = $state(true);
  let busy = $state(false);
  let err = $state<string | null>(null);
  let newName = $state('');

  async function load() {
    loading = true;
    err = null;
    try {
      const r = await fetchSnapshots(slug);
      items = r.items;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }
  onMount(load);

  async function onCreate() {
    busy = true;
    err = null;
    try {
      await createSnapshot(slug, newName.trim() || undefined);
      newName = '';
      await load();
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      busy = false;
    }
  }

  async function onRestore(s: DataStructureSnapshot) {
    const label = s.name || `від ${new Date(s.createdAt).toLocaleString('uk-UA')}`;
    if (!confirm(
      `ВІДНОВИТИ структуру до знімка "${label}"?\n\n` +
        `• Поля зі знімка (${s.fieldCount}) — відновляться точно.\n` +
        `• Поля додані після знімка — залишаться, але перемістяться у розділ "Без групи".\n` +
        `• Групи (${s.groupCount}) — замінять поточні.\n\n` +
        `Дані записів не видаляються.`,
    )) return;
    busy = true;
    err = null;
    try {
      await restoreSnapshot(slug, s.id);
      onChanged?.();
      await load();
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      busy = false;
    }
  }

  async function onDelete(s: DataStructureSnapshot) {
    if (!confirm(`Видалити знімок "${s.name || new Date(s.createdAt).toLocaleString()}"?`)) return;
    busy = true;
    try {
      await deleteSnapshot(slug, s.id);
      await load();
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      busy = false;
    }
  }
</script>

<div class="rounded-xl border border-[var(--color-border-3)] bg-white">
  <div class="px-4 py-3 border-b border-[var(--color-border-3)]">
    <div class="text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">
      Знімки структури
    </div>
    <div class="mt-0.5 text-[10.5px] text-[var(--color-text-muted-2)]">
      Збережіть поточний стан полів і груп. При відновленні — поля зі знімка повертаються точно,
      нові поля (додані після) залишаються але переміщуються у «Без групи». Дані не видаляються.
    </div>
  </div>

  <div class="px-4 py-3 border-b border-[var(--color-border-3)] flex gap-2">
    <input
      bind:value={newName}
      placeholder="Назва знімка (опційно) — напр. 'до експерименту з групами'"
      disabled={busy}
      class="flex-1 rounded-md border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs"
    />
    <button
      type="button"
      onclick={onCreate}
      disabled={busy}
      class="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-pu)] text-white px-3 py-1.5 text-[11px] font-semibold disabled:opacity-50"
    ><Icon name="camera" size={13} /> Зберегти знімок</button>
  </div>

  {#if err}
    <div class="mx-4 my-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700">{err}</div>
  {/if}

  {#if loading}
    <div class="px-4 py-6 text-center text-xs text-[var(--color-text-muted)]">Завантаження…</div>
  {:else if items.length === 0}
    <div class="px-4 py-6 text-center text-xs text-[var(--color-text-muted-2)] italic">
      Знімків поки немає
    </div>
  {:else}
    <div class="divide-y divide-[var(--color-border-3)]">
      {#each items as s (s.id)}
        <div class="px-4 py-2.5 flex items-center gap-3 text-xs hover:bg-[var(--color-bg-3)]/40">
          <div class="flex-1 min-w-0">
            <div class="font-semibold truncate">
              {s.name || `Знімок від ${new Date(s.createdAt).toLocaleString('uk-UA')}`}
            </div>
            <div class="text-[10.5px] text-[var(--color-text-muted)]">
              {new Date(s.createdAt).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' })}
              · полів: {s.fieldCount} · груп: {s.groupCount}
            </div>
          </div>
          <button
            type="button"
            onclick={() => onRestore(s)}
            disabled={busy}
            class="inline-flex items-center gap-1 rounded-md border border-[var(--color-pu-2)] text-[var(--color-pu-3)] bg-white px-2.5 py-1 text-[11px] font-semibold hover:bg-[var(--color-pu)] hover:text-white disabled:opacity-50"
          ><Icon name="restore" size={13} /> Відновити</button>
          <button
            type="button"
            onclick={() => onDelete(s)}
            disabled={busy}
            class="inline-flex items-center rounded-md text-[var(--color-text-muted-2)] hover:text-rose-600"
            title="Видалити знімок"
          ><Icon name="delete" size={15} /></button>
        </div>
      {/each}
    </div>
  {/if}
</div>
